// src/services/authService.ts
import { post } from "../helpers/apiHelper";
import { Api } from "../constants";
import { ForgotPasswordResponse } from "../types";
import type {
  AuthToken,
  CreateAdminRequest,
  ResetAdminPasswordRequest,
  LoginApiResult,
} from "../types/auth";
import { getUserIdFromAccessToken } from "../utils/helpers";
import { mapRefreshPayloadToAuthToken } from "../utils/authRefreshUtils";
import { ERROR_MESSAGES, LOGIN_API_RESULT_KIND } from "../utils/constants";
import { getSsoApiBaseUrl } from "../utils/apiUtils";

function throwLoginError(result: {
  status?: string;
  message?: string;
  statusCode?: number | string;
  isBlockedByAdmin?: boolean;
}) {
  const errorMessage = result?.message || ERROR_MESSAGES.LOGIN_FAILED;
  const raw = result?.statusCode;
  let statusCode = 500;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    statusCode = raw;
  } else if (typeof raw === "string") {
    const num = Number(raw);
    if (Number.isFinite(num)) {
      statusCode = num;
    }
  }
  const error = new Error(errorMessage) as any;
  error.response = {
    data: {
      status: result?.status || "ERROR",
      statusCode,
      message: errorMessage,
      ...(result?.isBlockedByAdmin === true
        ? { isBlockedByAdmin: true }
        : {}),
    },
  };
  throw error;
}

// Login API
export async function loginApi(
  email: string,
  password: string
): Promise<LoginApiResult> {
  const url = `${Api.login}`;
  const result = await post<Record<string, unknown>>(url, { email, password });

  const responseData =
    result && typeof result.data === "object" && result.data !== null
      ? (result.data as Record<string, unknown>)
      : result;
  const challengeSource =
    typeof responseData?.challenge === "string"
      ? responseData.challenge
      : typeof result?.challenge === "string"
        ? result.challenge
        : "";
  const challenge = challengeSource.toLowerCase();
  const session =
    typeof responseData?.session === "string"
      ? responseData.session
      : typeof responseData?.sessionToken === "string"
        ? responseData.sessionToken
        : typeof result?.session === "string"
          ? result.session
          : typeof result?.sessionToken === "string"
            ? result.sessionToken
            : "";
  if (
    challenge === LOGIN_API_RESULT_KIND.NEW_PASSWORD_REQUIRED &&
    session.trim() !== ""
  ) {
    return {
      kind: challenge,
      challenge,
      session,
    };
  }

  if (result?.status === "ERROR" || !result?.access_token) {
    throwLoginError(
      result as {
        status?: string;
        message?: string;
        statusCode?: number | string;
        isBlockedByAdmin?: boolean;
      }
    );
  }

  const access_token = String(result.access_token);
  return {
    kind: LOGIN_API_RESULT_KIND.SUCCESS,
    access_token,
    id_token: String(result.id_token ?? ""),
    refresh_token: String(result.refresh_token ?? ""),
    expires_in:
      typeof result.expires_in === "number"
        ? result.expires_in
        : result.expires_in != null
          ? Number(result.expires_in) || null
          : null,
    token_type:
      typeof result.token_type === "string" ? result.token_type : undefined,
    userId: getUserIdFromAccessToken(access_token),
  };
}

export async function createAdminByAdminApi(
  body: CreateAdminRequest
): Promise<void> {
  const url = `${Api.adminCreate}`;
  await post(url, body);
}

export async function resetAdminPasswordApi(
  body: ResetAdminPasswordRequest
): Promise<AuthToken> {
  const url = `${Api.adminResetPassword}`;
  const result = await post<{
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
    expires_in?: number | null;
  }>(url, body, undefined, undefined, false, false, true);

  if (!result?.access_token || !result?.refresh_token) {
    const errorMessage =
      (result as any)?.message || ERROR_MESSAGES.LOGIN_FAILED;
    const error = new Error(errorMessage) as any;
    error.response = {
      data: {
        status: "ERROR",
        statusCode: 401,
        message: errorMessage,
      },
    };
    throw error;
  }

  const access_token = String(result.access_token);
  return {
    id_token: String(result.id_token ?? ""),
    access_token,
    refresh_token: String(result.refresh_token),
    expires_in:
      typeof result.expires_in === "number"
        ? result.expires_in
        : result.expires_in != null
          ? Number(result.expires_in) || null
          : null,
    userId: getUserIdFromAccessToken(access_token),
  };
}

export async function refreshTokenApi(refreshToken: string): Promise<AuthToken> {
  const url = `${Api.refreshToken}`;
  const result = await post<{
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
    expires_in?: number | null;
    token_type?: string;
  }>(url, { refreshToken }, undefined, undefined, false, false, true);

  if (!result?.access_token || !result?.refresh_token) {
    const errorMessage = ERROR_MESSAGES.USER_TOKEN_MISSING;
    const error = new Error(errorMessage) as any;
    error.response = {
      data: {
        status: "ERROR",
        statusCode: 401,
        message: errorMessage,
      },
    };
    throw error;
  }

  return mapRefreshPayloadToAuthToken(result);
}

// Signup API
export async function signupApi(data: {
  email: string;
  password: string;
  name: string;
  ageConfirmed?: boolean;
  phoneNumber?: string;
  dob?: string;
}) {
  const url = `${Api.signup}`;
  const payload: {
    email: string;
    password: string;
    name: string;
    ageConfirmed?: boolean;
    phoneNumber?: string;
    dob?: string;
  } = {
    email: data.email,
    password: data.password,
    name: data.name,
  };
  if (data.ageConfirmed) {
    payload.ageConfirmed = true;
  }
  if (data.phoneNumber) {
    payload.phoneNumber = data.phoneNumber;
  }
  if (data.dob) {
    payload.dob = data.dob;
  }
  return await post(url, payload);
}

export async function verifyEmailApi(email: string, verificationCode: string) {
  const url = `${Api.verifyEmail}?email=${encodeURIComponent(email)}&verificationCode=${verificationCode}`;
  return await post(url);
}

export async function resendConfirmationCodeApi(email: string) {
  const url = `${Api.resendConfirmationCode}?email=${encodeURIComponent(email)}`;
  return await post(url);
}

export async function confirmForgotPasswordApi(
  email: string,
  code: string,
  newPassword: string
) {
  const url = `${Api.confirmForgotPassword}`;
  const data = { email, confirmationCode: code, newPassword };
  return await post(url, data);
}

export async function forgotPasswordApi(
  email: string
): Promise<ForgotPasswordResponse> {
  const url = `${Api.forgotPassword}?email=${encodeURIComponent(email)}`;
  return await post<ForgotPasswordResponse>(url);
}

export async function changePasswordApi(
  oldPassword: string,
  newPassword: string
) {
  const url = `${Api.changePassword}`;
  const result = await post(url,{ oldPassword, newPassword });
  return result;
}

export async function ssoLoginApi(code: string, redirectUri: string) {
  const url = `${getSsoApiBaseUrl()}/auth/validate`;
  const result = await post(url, { code, redirectUri });

  return {
    access_token: result.access_token,
    id_token: result.id_token,
    refresh_token: result.refresh_token,
    expires_in: result.expires_in,
    token_type: result.token_type,
  };
}
